import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MenuListProps } from 'react-select';

// Height calculation: padding-top (8px) + line-height (~18px) + padding-bottom (8px) + border (1px) = ~35px
const ITEM_HEIGHT = 35; // Height of each dropdown item (matches #dropdown-item styling)
const MAX_VISIBLE_ITEMS = 8; // Number of items visible at once
const BUFFER_SIZE = 3; // Extra items to render above and below visible area

const VirtualizedMenuList: React.FC<MenuListProps> = ({
    children,
    maxHeight,
    getValue,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemHeightRef = useRef<number>(ITEM_HEIGHT);
    const [scrollTop, setScrollTop] = useState(0);
    const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

    const childrenArray = React.Children.toArray(children);

    // Use measured height if available, otherwise use default
    const actualItemHeight = measuredHeight || itemHeightRef.current;
    const totalHeight = childrenArray.length * actualItemHeight;
    const visibleHeight = Math.min(
        maxHeight || 200,
        MAX_VISIBLE_ITEMS * actualItemHeight
    );

    // Calculate which items should be rendered
    const startIndex = Math.max(
        0,
        Math.floor(scrollTop / actualItemHeight) - BUFFER_SIZE
    );
    const endIndex = Math.min(
        childrenArray.length,
        Math.ceil((scrollTop + visibleHeight) / actualItemHeight) + BUFFER_SIZE
    );

    const visibleChildren = childrenArray.slice(startIndex, endIndex);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Measure actual item height on mount
    useEffect(() => {
        if (containerRef.current && childrenArray.length > 0) {
            // Wait for next frame to ensure DOM is rendered
            requestAnimationFrame(() => {
                const firstItem =
                    containerRef.current?.querySelector('#dropdown-item');
                if (firstItem) {
                    const { height } = firstItem.getBoundingClientRect();
                    if (height > 0 && height !== itemHeightRef.current) {
                        itemHeightRef.current = height;
                        setMeasuredHeight(height);
                    }
                }
            });
        }
    }, [childrenArray.length]);

    // Scroll to selected item on mount
    useEffect(() => {
        const selectedValue: any = getValue();
        if (selectedValue && selectedValue.length > 0 && containerRef.current) {
            const selectedIndex = childrenArray.findIndex(
                (child: any) =>
                    child?.props?.data?.value === selectedValue[0]?.value
            );

            if (selectedIndex !== -1) {
                const scrollPosition =
                    selectedIndex * actualItemHeight - visibleHeight / 2;
                containerRef.current.scrollTop = Math.max(0, scrollPosition);
                setScrollTop(Math.max(0, scrollPosition));
            }
        }
    }, [measuredHeight]);

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            className="custom-dropdown__menu-list"
            style={{
                maxHeight: visibleHeight,
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
            }}
        >
            {/* Spacer for items before visible range */}
            {startIndex > 0 && (
                <div style={{ height: startIndex * actualItemHeight }} />
            )}

            {/* Render visible items */}
            {visibleChildren}

            {/* Spacer for items after visible range */}
            {endIndex < childrenArray.length && (
                <div
                    style={{
                        height:
                            (childrenArray.length - endIndex) *
                            actualItemHeight,
                    }}
                />
            )}
        </div>
    );
};

export default VirtualizedMenuList;
